/**
 * Три chip-группы для каталога декора (Экраны 2.2.1 фильтр / 2.2.6 каталог).
 * Презентационный, БЕЗ fetch.
 *
 * ADR-0054: опции Подкатегория/Бренд/Тип приходят пропсами ИЗ ДАННЫХ (через
 * `useDecorFacets` → `/api/decors/:kind/facets`), а НЕ из статичных словарей
 * `*_BRANDS`/`*_DECOR_TYPES`. Прежняя статика разошлась с verbatim-значениями
 * CSV (K-stone≠K-Stone и т.п.) и не скоупилась под подкатегорию → выбор
 * «мёртвого» чипа давал 0 результатов. Бренд/Тип сужены выбранной подкатегорией
 * на стороне эндпоинта.
 *
 * P-033 здесь не применяется: компонент чисто рендерит переданные массивы строк,
 * не ветвится по kind и не держит per-kind vocabulary. Раздельность kind'ов
 * обеспечивается на стороне данных/эндпоинта (switch + filterX).
 */
export type DecorFilterField = 'subcategories' | 'brands' | 'types';

export interface DecorFilterChipsProps {
  /** Опции Подкатегории — непустые подкатегории kind (из полного пула, ADR-0054). */
  subcategoryOptions: readonly string[];
  /** Опции Бренда — distinct из данных, сужены выбранной подкатегорией. */
  brandOptions: readonly string[];
  /** Опции Типа — distinct из данных, сужены выбранной подкатегорией. */
  typeOptions: readonly string[];
  selectedSubcategories: readonly string[];
  selectedBrands: readonly string[];
  selectedTypes: readonly string[];
  onToggle: (field: DecorFilterField, value: string) => void;
  onResetGroup: (field: DecorFilterField) => void;
  /** Опции грузятся (facets fetch) — показываем подсказку в пустых группах. */
  optionsLoading?: boolean;
}

export function DecorFilterChips(props: DecorFilterChipsProps) {
  // ADR-0059: Бренд/Тип появляются только ПОСЛЕ выбора Подкатегории. До этого —
  // подсказка (опции брендов/типов без подкатегории были бы по всему kind и
  // путали бы каскад). Тип каскадит под выбранный Бренд (опции с бэка).
  const subcategoryChosen = props.selectedSubcategories.length > 0;

  return (
    <div className="flex flex-col gap-6">
      <ChipGroup
        groupLabel="Подкатегория"
        values={props.subcategoryOptions}
        selected={props.selectedSubcategories}
        loading={props.optionsLoading}
        onToggle={(v) => props.onToggle('subcategories', v)}
        onResetGroup={() => props.onResetGroup('subcategories')}
      />
      {subcategoryChosen ? (
        <>
          <ChipGroup
            groupLabel="Бренд"
            values={props.brandOptions}
            selected={props.selectedBrands}
            loading={props.optionsLoading}
            onToggle={(v) => props.onToggle('brands', v)}
            onResetGroup={() => props.onResetGroup('brands')}
          />
          <ChipGroup
            groupLabel="Тип"
            values={props.typeOptions}
            selected={props.selectedTypes}
            loading={props.optionsLoading}
            onToggle={(v) => props.onToggle('types', v)}
            onResetGroup={() => props.onResetGroup('types')}
          />
        </>
      ) : (
        <p className="text-muted text-base m-0">Выберите подкатегорию, чтобы выбрать бренд и тип</p>
      )}
    </div>
  );
}

interface ChipGroupProps {
  groupLabel: string;
  values: readonly string[];
  selected: readonly string[];
  loading?: boolean;
  onToggle: (value: string) => void;
  onResetGroup: () => void;
}

function ChipGroup({
  groupLabel,
  values,
  selected,
  loading,
  onToggle,
  onResetGroup,
}: ChipGroupProps) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="font-display text-filter-label text-ink">{groupLabel}</span>
        <button
          type="button"
          onClick={onResetGroup}
          aria-label={`Сбросить выбор: ${groupLabel}`}
          className="bg-transparent text-muted underline text-base"
        >
          Сбросить
        </button>
      </div>
      {values.length === 0 ? (
        <p className="text-muted text-base m-0">
          {loading ? 'Загружаем…' : 'Нет доступных значений'}
        </p>
      ) : (
        <div className="flex flex-wrap gap-3">
          {values.map((value) => {
            const isSelected = selected.includes(value);
            return (
              <button
                key={value}
                type="button"
                aria-pressed={isSelected}
                aria-label={`${groupLabel}: ${value}`}
                onClick={() => onToggle(value)}
                className={[
                  'min-h-tap-chip-lg px-5 rounded-chip text-chip transition-colors',
                  'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none',
                  isSelected
                    ? 'bg-chip-selected text-white border-[1.5px] border-chip-selected'
                    : 'bg-card text-ink border-[1.5px] border-accent active:opacity-90',
                ].join(' ')}
              >
                {value}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
